import type { ArchetypeDefinition, ArchetypeId } from './types';

export const archetypes = {
  'broad-match': {
    id: 'broad-match',
    label: 'Broad Match',
    funnelStage: 'Awareness / Exploration',
    description: 'Broad topic overview for exploratory traffic and top-of-funnel campaigns.',
    primaryCta: 'Explore category',
    hasForm: false,
    priceVisible: true,
    requiredSections: ['hero_section', 'topics_explorer_section', 'featured_courses_section', 'faq_section'],
    optionalSections: [
      'stats_strip_section',
      'audience_cards_section',
      'formats_section',
      'certifications_section',
      'benefits_grid_section',
      'logo_strip_section',
      'corporate_cta_section',
      'experts_section',
      'footer_section',
    ],
    defaultOrder: [
      'hero_section',
      'stats_strip_section',
      'topics_explorer_section',
      'audience_cards_section',
      'featured_courses_section',
      'formats_section',
      'certifications_section',
      'benefits_grid_section',
      'logo_strip_section',
      'corporate_cta_section',
      'experts_section',
      'faq_section',
      'footer_section',
    ],
  },
  'lead-gen': {
    id: 'lead-gen',
    label: 'Lead Gen',
    funnelStage: 'Consideration',
    description: 'Single-product landing page focused on lead capture and qualification.',
    primaryCta: 'Submit form',
    hasForm: true,
    priceVisible: false,
    requiredSections: ['hero_section', 'form_section', 'faq_section'],
    optionalSections: [
      'trust_bar_section',
      'benefits_section',
      'stats_section',
      'modules_section',
      'audience_section',
      'testimonials_section',
      'trainers_section',
      'footer_section',
      'sticky_cta_bar',
      'whatsapp_widget',
    ],
    defaultOrder: [
      'hero_section',
      'trust_bar_section',
      'benefits_section',
      'stats_section',
      'modules_section',
      'audience_section',
      'testimonials_section',
      'trainers_section',
      'form_section',
      'faq_section',
      'footer_section',
      'sticky_cta_bar',
      'whatsapp_widget',
    ],
    allowedVariants: {
      hero_section: ['lead', 'sea', 'split-dark'],
      faq_section: ['standard', 'dual-cta', 'minimal'],
    },
  },
  'direct-conversion': {
    id: 'direct-conversion',
    label: 'Direct Conversion',
    funnelStage: 'Decision / Purchase',
    description: 'Course-category landing page with visible prices and direct booking CTAs.',
    primaryCta: 'Book now',
    hasForm: false,
    priceVisible: true,
    requiredSections: ['hero_section', 'featured_courses_section', 'faq_section'],
    optionalSections: [
      'trust_bar_section',
      'stats_section',
      'benefits_grid_section',
      'testimonials_section',
      'footer_section',
      'sticky_cta_bar',
    ],
    defaultOrder: [
      'hero_section',
      'trust_bar_section',
      'featured_courses_section',
      'stats_section',
      'benefits_grid_section',
      'testimonials_section',
      'faq_section',
      'footer_section',
      'sticky_cta_bar',
    ],
  },
  'event-webinar': {
    id: 'event-webinar',
    label: 'Event / Webinar',
    funnelStage: 'Awareness / Registration',
    description: 'Event registration landing page with agenda, speakers, urgency, and a compact form.',
    primaryCta: 'Register',
    hasForm: true,
    priceVisible: false,
    requiredSections: ['hero_section', 'form_section', 'faq_section'],
    optionalSections: [
      'trust_bar_section',
      'stats_section',
      'testimonials_section',
      'footer_section',
      'sticky_cta_bar',
    ],
    defaultOrder: [
      'hero_section',
      'trust_bar_section',
      'modules_section',
      'trainers_section',
      'audience_section',
      'stats_section',
      'testimonials_section',
      'form_section',
      'faq_section',
      'footer_section',
      'sticky_cta_bar',
    ],
  },
  'corporate-b2b': {
    id: 'corporate-b2b',
    label: 'Corporate / B2B Inhouse',
    funnelStage: 'Enterprise Sales',
    description: 'B2B landing page for inhouse training, enterprise qualification, and quote requests.',
    primaryCta: 'Request quote',
    hasForm: true,
    priceVisible: false,
    requiredSections: ['corporate_hero_section', 'corporate_contact_form_section', 'corporate_faq_section'],
    optionalSections: [
      'corporate_sticky_nav_section',
      'certifications_strip_section',
      'corporate_benefits_section',
      'training_areas_section',
      'process_steps_section',
      'methodology_section',
      'client_names_section',
      'corporate_conversion_cta_section',
      'corporate_footer_section',
    ],
    defaultOrder: [
      'corporate_sticky_nav_section',
      'corporate_hero_section',
      'certifications_strip_section',
      'corporate_benefits_section',
      'training_areas_section',
      'process_steps_section',
      'methodology_section',
      'client_names_section',
      'corporate_faq_section',
      'corporate_conversion_cta_section',
      'corporate_contact_form_section',
      'corporate_footer_section',
    ],
  },
} satisfies Record<ArchetypeId, ArchetypeDefinition>;

export function getArchetypeDefinition(id: ArchetypeId): ArchetypeDefinition {
  return archetypes[id];
}

export function getArchetypeDefinitions(): ArchetypeDefinition[] {
  return Object.values(archetypes);
}
